// Meetings header with filters and navigation
import React, { useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Text, Button, Dropdown, Option, OptionGroup, Input, Tag } from '@fluentui/react-components';
import { SearchRegular, DismissRegular, ErrorCircleFilled } from '@fluentui/react-icons';
import { Box, useFluentTokens, withDesignTokens } from '../../../../theme/fluent';
import { useAccessibleCalendars } from '../../hooks/data/useDiaryManagementData';
import { useMsal } from '@azure/msal-react';
import { formatToISO, getStartOfWeek, getEndOfWeek, getCurrentWorkWeek } from '../../utils/dateHelpers';
import { TYPE_FILTER_OPTIONS, TYPE_FILTER_CATEGORIES, TypeFilterValue } from './meetingFilters';

// Define filter hierarchy for easier state management
const PARENT_TO_CHILDREN_MAP: Record<string, TypeFilterValue[]> = {
  allOrganised: TYPE_FILTER_OPTIONS.filter(
    (opt) => opt.category === 'Organised meetings' && opt.value !== 'allOrganised',
  ).map((opt) => opt.value),
  allInvited: TYPE_FILTER_OPTIONS.filter((opt) => opt.category === 'Attending' && opt.value !== 'allInvited').map(
    (opt) => opt.value,
  ),
};

const CHILD_TO_PARENT_MAP: Record<string, string> = Object.fromEntries(
  Object.entries(PARENT_TO_CHILDREN_MAP).flatMap(([parent, children]) => children.map((child) => [child, parent])),
);

interface MeetingsHeaderProps {
  calendar: string;
  startDate: string;
  endDate: string;
  hasDateParams: boolean;
  searchTerm: string;
  onSearchChange: (term: string) => void;
  isLoadingCalendars: boolean;
}

export const MeetingsHeader: React.FC<MeetingsHeaderProps> = ({
  calendar,
  startDate,
  endDate,
  hasDateParams,
  searchTerm,
  onSearchChange,
  isLoadingCalendars,
}) => {
  const { tokens, modeTokens } = useFluentTokens();
  const styles = useMemo(() => withDesignTokens(tokens), [tokens]);
  const [searchParams, setSearchParams] = useSearchParams();
  const { accounts } = useMsal();
  const { data: calendarsData } = useAccessibleCalendars();

  const currentUserEmail = accounts.length > 0 ? accounts[0].username : undefined;

  const selectedCalendars = useMemo(() => {
    if (!calendar || calendar === 'all') return [];
    return calendar.split(',').filter(Boolean);
  }, [calendar]);

  const selectedTypes = useMemo(() => {
    const typeParam = searchParams.get('type');
    if (!typeParam) return [];
    return typeParam.split(',').filter(Boolean) as TypeFilterValue[];
  }, [searchParams]);

  // Determine if we're in day or week view
  // If no date params, check for explicit view param, default to week
  // If date params exist, infer from start === end
  const actualViewType = useMemo(() => {
    if (!hasDateParams) {
      // No dates in URL - check for explicit view preference
      const viewParam = searchParams.get('viewType');
      return viewParam === 'day' ? 'day' : 'week';
    }
    // Has dates - infer from whether start === end
    const start = new Date(startDate);
    const end = new Date(endDate);
    start.setHours(0, 0, 0, 0);
    end.setHours(0, 0, 0, 0);
    return start.getTime() === end.getTime() ? 'day' : 'week';
  }, [startDate, endDate, hasDateParams, searchParams]);

  const handleViewChange = (newView: 'day' | 'week') => {
    const params = new URLSearchParams(searchParams);

    if (!hasDateParams) {
      // Currently showing today/current week - just toggle the view type
      params.set('viewType', newView);
      params.delete('start');
      params.delete('end');
    } else {
      // Has specific dates - adjust them for the new view
      if (newView === 'week') {
        // Switch to week view - use week containing current startDate
        const weekStart = getStartOfWeek(new Date(startDate));
        const weekEnd = getEndOfWeek(weekStart);

        // Check if it's the current week
        const currentWeek = getCurrentWorkWeek();
        if (
          formatToISO(weekStart) === formatToISO(currentWeek.start) &&
          formatToISO(weekEnd) === formatToISO(currentWeek.end)
        ) {
          params.delete('start');
          params.delete('end');
          params.delete('viewType');
        } else {
          params.set('start', formatToISO(weekStart));
          params.set('end', formatToISO(weekEnd));
          params.delete('viewType');
        }
      } else {
        // Switch to day view - use the first day of current week view
        const selectedDay = new Date(startDate);
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        if (selectedDay.getTime() === today.getTime()) {
          params.delete('start');
          params.delete('end');
          params.set('viewType', 'day');
        } else {
          params.set('start', formatToISO(selectedDay));
          params.set('end', formatToISO(selectedDay));
          params.delete('viewType');
        }
      }
    }

    setSearchParams(params);
  };

  const handleCalendarChange = useCallback(
    (_: unknown, data: { optionValue?: string; selectedOptions: string[] }) => {
      const params = new URLSearchParams(searchParams);

      if (data.selectedOptions.length === 0) {
        params.delete('calendar');
      } else {
        params.set('calendar', data.selectedOptions.join(','));
      }

      setSearchParams(params);
    },
    [searchParams, setSearchParams],
  );

  const handleTypeChange = useCallback(
    (_: unknown, data: { optionValue?: string; selectedOptions: string[] }) => {
      const params = new URLSearchParams(searchParams);
      const { optionValue, selectedOptions } = data;
      if (!optionValue) return;

      const newSelected = new Set(selectedOptions as TypeFilterValue[]);
      const toggledOption = optionValue as TypeFilterValue;

      // If a parent option (e.g., 'allOrganised') was toggled
      if (toggledOption in PARENT_TO_CHILDREN_MAP) {
        const children = PARENT_TO_CHILDREN_MAP[toggledOption];
        if (newSelected.has(toggledOption)) {
          children.forEach((opt) => newSelected.add(opt));
        } else {
          children.forEach((opt) => newSelected.delete(opt));
        }
      }
      // If a child option was toggled, sync its parent's state
      else if (toggledOption in CHILD_TO_PARENT_MAP) {
        const parent = CHILD_TO_PARENT_MAP[toggledOption] as TypeFilterValue;
        const siblings = PARENT_TO_CHILDREN_MAP[parent];
        const allSiblingsSelected = siblings.every((opt) => newSelected.has(opt));

        if (allSiblingsSelected) {
          newSelected.add(parent);
        } else {
          newSelected.delete(parent);
        }
      }

      const resolvedOptions = Array.from(newSelected);
      if (resolvedOptions.length === 0) {
        params.delete('type');
      } else {
        params.set('type', resolvedOptions.join(','));
      }

      setSearchParams(params);
    },
    [searchParams, setSearchParams],
  );

  // Format calendar options with "(You)" suffix and sort alphabetically by surname
  const calendarOptions = useMemo(() => {
    if (!calendarsData?.calendars) {
      return [];
    }

    return calendarsData.calendars.map((cal) => {
      const isUser = currentUserEmail && cal.email.toLowerCase() === currentUserEmail.toLowerCase();
      return {
        email: cal.email,
        name: isUser ? `${cal.name} (You)` : cal.name,
      };
    });
  }, [calendarsData, currentUserEmail]);

  // Count visible type tags (1 for 'all', or actual selected count)
  const typeFilterCount = useMemo(() => {
    const selected = new Set(selectedTypes);
    let visibleTagCount = 0;

    if (selected.has('allOrganised')) {
      visibleTagCount++;
      const organisedChildren = TYPE_FILTER_OPTIONS.filter(
        (opt) => opt.category === 'Organised meetings' && opt.value !== 'allOrganised',
      ).map((opt) => opt.value);
      organisedChildren.forEach((opt) => selected.delete(opt));
      selected.delete('allOrganised');
    }

    if (selected.has('allInvited')) {
      visibleTagCount++;
      const attendingChildren = TYPE_FILTER_OPTIONS.filter(
        (opt) => opt.category === 'Attending' && opt.value !== 'allInvited',
      ).map((opt) => opt.value);
      attendingChildren.forEach((opt) => selected.delete(opt));
      selected.delete('allInvited');
    }

    // Add the count of remaining individual tags
    visibleTagCount += selected.size;

    return visibleTagCount;
  }, [selectedTypes]);

  const hasFreeSlotSelected = selectedTypes.includes('freeSlot');
  const hasOtherTypesSelected = useMemo(() => {
    return selectedTypes.some((type) => type !== 'freeSlot');
  }, [selectedTypes]);

  // Helper to determine if an option should be disabled
  const isOptionDisabled = useCallback(
    (value: TypeFilterValue) =>
      (value === 'freeSlot' && hasOtherTypesSelected) || (value !== 'freeSlot' && hasFreeSlotSelected),
    [hasFreeSlotSelected, hasOtherTypesSelected],
  );

  return (
    <Box
      display="flex"
      flexDirection="column"
      gap="m"
      style={{
        paddingBottom: tokens.spacingVerticalS,
      }}
    >
      {/* Title and filters row */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        style={{
          flexWrap: 'wrap',
          gap: tokens.spacingHorizontalM,
          rowGap: tokens.spacingVerticalS,
        }}
      >
        <Text
          size={400}
          weight="regular"
          style={{
            fontSize: tokens.fontSizeTitle3,
            lineHeight: tokens.lineHeightTitle3,
            color: modeTokens.colorNeutralForeground1,
            flexShrink: 0,
            padding: tokens.spacingHorizontalL,
          }}
        >
          Meetings
        </Text>

        <Box
          display="flex"
          gap="s"
          alignItems="center"
          style={{
            flex: '1 1 auto',
            minWidth: 0,
            justifyContent: 'center',
            flexWrap: 'wrap',
          }}
        >
          {/* Calendar multi-select dropdown — only when multiple accounts linked */}
          {calendarOptions.length > 1 && (
            <Dropdown
              appearance="filled-darker"
              placeholder="Accounts"
              multiselect
              value={selectedCalendars.length > 0 ? `Accounts (${selectedCalendars.length})` : ''}
              selectedOptions={selectedCalendars}
              onOptionSelect={handleCalendarChange}
              disabled={isLoadingCalendars}
              aria-label={`Account filter, ${selectedCalendars.length} selected`}
              style={{
                minWidth: '186px',
                maxWidth: '200px',
                flexShrink: 1,
                backgroundColor: modeTokens.colorNeutralBackground3,
                ...styles.dropdown(),
              }}
            >
              {calendarOptions.map((opt) => (
                <Option
                  key={opt.email}
                  value={opt.email}
                  checkIcon={{
                    style: { marginRight: tokens.spacingHorizontalXS, borderRadius: tokens.borderRadiusMedium },
                  }}
                  aria-label={`Filter by ${opt.name}`}
                >
                  {opt.name}
                </Option>
              ))}
            </Dropdown>
          )}

          {/* Type multi-select dropdown */}
          <Dropdown
            appearance="filled-darker"
            placeholder="Type"
            multiselect
            value={typeFilterCount > 0 ? `Type (${typeFilterCount})` : ''}
            selectedOptions={selectedTypes}
            onOptionSelect={handleTypeChange}
            disabled={isLoadingCalendars}
            aria-label={`Type filter, ${typeFilterCount} selected`}
            style={{
              minWidth: '186px',
              maxWidth: '250px',
              flexShrink: 1,
              backgroundColor: modeTokens.colorNeutralBackground3,
              ...styles.dropdown(),
            }}
          >
            {TYPE_FILTER_CATEGORIES.map((category) => (
              <OptionGroup key={category} label={category}>
                {TYPE_FILTER_OPTIONS.filter((opt) => opt.category === category).map((opt) => (
                  <Option
                    key={opt.value}
                    value={opt.value}
                    disabled={isOptionDisabled(opt.value)}
                    checkIcon={{
                      style: { marginRight: tokens.spacingHorizontalXS, borderRadius: tokens.borderRadiusMedium },
                    }}
                    aria-label={`Filter by ${opt.label}`}
                  >
                    {opt.label}
                  </Option>
                ))}
              </OptionGroup>
            ))}
          </Dropdown>

          {/* Search bar - always visible */}
          <Box
            style={{
              position: 'relative',
              minWidth: '180px',
              width: '220px',
              flexShrink: 1,
            }}
          >
            <Input
              appearance="filled-darker"
              value={searchTerm}
              onChange={(_, data) => {
                const limitedValue = data.value.length > 200 ? data.value.slice(0, 200) : data.value;
                onSearchChange(limitedValue);
              }}
              placeholder="Search"
              contentBefore={<SearchRegular />}
              contentAfter={
                searchTerm ? (
                  <Button
                    appearance="transparent"
                    icon={<DismissRegular />}
                    size="small"
                    onClick={() => onSearchChange('')}
                    aria-label="Clear search"
                  />
                ) : undefined
              }
              maxLength={200}
              style={{
                ...styles.searchBox(),
                ...(searchTerm.length >= 200 && {
                  borderColor: modeTokens.colorRedBorder,
                }),
              }}
            />
            {searchTerm.length >= 200 && (
              <Box
                display="flex"
                gap="xxs"
                alignItems="center"
                style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  marginTop: tokens.spacingVerticalXXS,
                  zIndex: 1,
                }}
              >
                <ErrorCircleFilled
                  style={{
                    fontSize: '12px',
                    color: modeTokens.colorRedForeground,
                    flexShrink: 0,
                  }}
                />
                <Text
                  size={200}
                  style={{
                    color: modeTokens.colorRedForeground,
                    fontSize: tokens.fontSizeBase200,
                    lineHeight: tokens.lineHeightBase200,
                    whiteSpace: 'nowrap',
                  }}
                >
                  You can enter maximum 200 characters
                </Text>
              </Box>
            )}
          </Box>
        </Box>

        {/* View tabs */}
        <Box
          display="flex"
          gap="xs"
          alignItems="center"
          style={{
            flexShrink: 0,
            padding: tokens.spacingHorizontalL,
          }}
        >
          <Tag
            shape="rounded"
            appearance="outline"
            onClick={() => handleViewChange('day')}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleViewChange('day');
              }
            }}
            role="button"
            tabIndex={0}
            aria-pressed={actualViewType === 'day'}
            aria-label="Switch to day view"
            style={{
              cursor: 'pointer',
              borderRadius: '9999px',
              fontSize: tokens.fontSizeBase300,
              fontWeight: tokens.fontWeightSemibold,
              lineHeight: tokens.lineHeightBase300,
              paddingLeft: '10px',
              paddingRight: '10px',
              paddingTop: '6px',
              paddingBottom: '6px',
              height: '32px',
              ...(actualViewType === 'day'
                ? {
                    border: `1px solid ${modeTokens.colorGreenBorder}`,
                    backgroundColor: modeTokens.colorGreenBackground,
                    color: modeTokens.colorGreenForeground,
                  }
                : {
                    border: 'none',
                    backgroundColor: 'transparent',
                    color: modeTokens.colorNeutralForeground2,
                  }),
            }}
          >
            Day
          </Tag>
          <Tag
            shape="rounded"
            appearance="outline"
            onClick={() => handleViewChange('week')}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleViewChange('week');
              }
            }}
            role="button"
            tabIndex={0}
            aria-pressed={actualViewType === 'week'}
            aria-label="Switch to week view"
            style={{
              cursor: 'pointer',
              borderRadius: '9999px',
              fontSize: tokens.fontSizeBase300,
              fontWeight: tokens.fontWeightSemibold,
              lineHeight: tokens.lineHeightBase300,
              paddingLeft: '10px',
              paddingRight: '10px',
              paddingTop: '6px',
              paddingBottom: '6px',
              height: '32px',
              ...(actualViewType === 'week'
                ? {
                    border: `1px solid ${modeTokens.colorGreenBorder}`,
                    backgroundColor: modeTokens.colorGreenBackground,
                    color: modeTokens.colorGreenForeground,
                  }
                : {
                    border: 'none',
                    backgroundColor: 'transparent',
                    color: modeTokens.colorNeutralForeground2,
                  }),
            }}
          >
            Week
          </Tag>
        </Box>
      </Box>
    </Box>
  );
};
